import { FormEvent, useMemo, useState, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useProcessTemplateDetail } from '../hooks/useProcessTemplateDetail';
import type { ProcessStepResourceInput, WorkProcessStep } from '../types/processTemplate.types';

const blankResource = { resource_type: 'material', resource_name: '', unit: '', norm_quantity: 1, waste_factor: 0, unit_price: 0 };
const categoryLabels: Record<string, string> = {
  construction: 'Thi công',
  maintenance: 'Bảo dưỡng',
  lighting: 'Chiếu sáng',
  hardscape: 'Hardscape',
  other: 'Khác',
};

export function ProcessTemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { template, isLoading, createStep, updateStep, deleteStep, upsertStepResources, isSavingStep, isDeletingStep, isSavingResources } = useProcessTemplateDetail(id);
  const [editingStep, setEditingStep] = useState<WorkProcessStep | null>(null);
  const [showStepForm, setShowStepForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkProcessStep | null>(null);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  const totalResources = useMemo(() => (template?.steps || []).reduce((sum, step) => sum + (step.resources || []).length, 0), [template]);

  if (isLoading) {
    return <div className="p-6 sm:text-sm text-xs text-slate-500">Đang tải quy trình...</div>;
  }

  if (!template) {
    return <EmptyState title="Không tìm thấy quy trình" description="Quy trình mẫu không tồn tại hoặc bạn chưa có quyền truy cập." />;
  }

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteStep(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="shrink-0 p-4">
        <PageHeaderNew title={template.template_name} subtitle={`${template.template_code} | ${categoryLabels[template.category_code] || template.category_code}`} backUrl="/estimates/process-templates" className="mb-3" />
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="info">{template.base_unit}</Badge>
          <Badge variant={template.status_code === 'active' ? 'success' : 'neutral'}>{template.status_code}</Badge>
          <Badge variant="neutral">{template.steps?.length || 0} bước</Badge>
          <Badge variant="neutral">{totalResources} tài nguyên</Badge>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/estimates/process-templates/${template.id}/edit`)}>
            <i className="ri-edit-line mr-2" />
            Sửa thông tin
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        <Card className="mb-4 border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="sm:text-sm text-xs font-medium text-slate-900">Mô tả quy trình</h2>
              <p className="mt-1 sm:text-sm text-xs text-slate-600">{template.description || 'Chưa có mô tả.'}</p>
            </div>
            <AuthButton module="estimates" action="update" onClick={() => { setEditingStep(null); setShowStepForm(true); }}>
              <i className="ri-add-line mr-2" />
              Thêm bước
            </AuthButton>
          </div>
        </Card>

        {showStepForm && (
          <StepForm
            templateId={template.id}
            companyId={template.company_id}
            step={editingStep}
            isSaving={isSavingStep}
            onCancel={() => { setShowStepForm(false); setEditingStep(null); }}
            onSubmit={async (payload) => {
              if (editingStep) await updateStep({ id: editingStep.id, updates: payload });
              else await createStep(payload);
              setShowStepForm(false);
              setEditingStep(null);
            }}
          />
        )}

        {(template.steps || []).length === 0 ? (
          <EmptyState title="Chưa có bước quy trình" description="Thêm bước đầu tiên như đào hố, chuẩn bị giá thể, trồng cây, chống cọc hoặc tưới dưỡng." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left sm:text-sm text-xs font-medium text-slate-900 w-1/3">Công tác / Tài nguyên</th>
                  <th className="px-4 py-3 text-left sm:text-sm text-xs font-medium text-slate-900">Mã / Phân loại</th>
                  <th className="px-4 py-3 text-right sm:text-sm text-xs font-medium text-slate-900">ĐVT</th>
                  <th className="px-4 py-3 text-right sm:text-sm text-xs font-medium text-slate-900">Định mức</th>
                  <th className="px-4 py-3 text-right sm:text-sm text-xs font-medium text-slate-900">Hao hụt</th>
                  <th className="px-4 py-3 text-right sm:text-sm text-xs font-medium text-slate-900">Đơn giá tham khảo</th>
                  <th className="px-4 py-3 text-right sm:text-sm text-xs font-medium text-slate-900 w-[120px]">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {(template.steps || []).map((step) => (
                  <Fragment key={step.id}>
                    {/* Parent Step Row */}
                    <tr className="bg-slate-50/80 hover:bg-slate-50 group">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{step.step_name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Mã: {step.step_code || '--'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="info" className="text-[10px]">{step.estimate_item_mode === 'parent' ? 'Hạng mục' : step.estimate_item_mode === 'child' ? 'Công tác' : 'Tài nguyên'}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-700 sm:text-sm text-xs">{step.unit}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-700 sm:text-sm text-xs">{(step.default_quantity || 0).toLocaleString('vi-VN')}</td>
                      <td className="px-4 py-3 text-right text-slate-400">-</td>
                      <td className="px-4 py-3 text-right text-slate-400">-</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button variant="ghost" size="icon" onClick={() => { setEditingStep(step); setShowStepForm(true); }} title="Sửa bước"><i className="ri-edit-line text-blue-600"/></Button>
                          <Button variant="ghost" size="icon" onClick={() => setExpandedStepId(expandedStepId === step.id ? null : step.id)} title="Cập nhật tài nguyên"><i className="ri-tools-line text-emerald-600"/></Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(step)} title="Xóa bước"><i className="ri-delete-bin-line text-rose-600"/></Button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Resource Editor Mode */}
                    {expandedStepId === step.id ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 bg-white border-l-2 border-emerald-500 shadow-inner">
                           <ResourceEditor step={step} isSaving={isSavingResources} onSave={async (resources) => { await upsertStepResources({ stepId: step.id, resources }); setExpandedStepId(null); }} onCancel={() => setExpandedStepId(null)} />
                        </td>
                      </tr>
                    ) : (
                      /* Readonly Resource Rows */
                      (step.resources || []).map(resource => (
                        <tr key={resource.id} className="hover:bg-slate-50/50">
                          <td className="px-4 py-2.5 pl-10">
                            <div className="flex items-center gap-2">
                              <i className="ri-corner-down-right-line text-slate-400"></i>
                              <span className="sm:text-sm text-xs text-slate-700">{resource.resource_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <Badge variant="neutral" className="text-[10px] capitalize">{resource.resource_type}</Badge>
                          </td>
                          <td className="px-4 py-2.5 text-right sm:text-sm text-xs text-slate-600">{resource.unit}</td>
                          <td className="px-4 py-2.5 text-right sm:text-sm text-xs text-slate-600">{(resource.norm_quantity || 0).toLocaleString('vi-VN')}</td>
                          <td className="px-4 py-2.5 text-right sm:text-sm text-xs text-slate-600">{resource.waste_factor}%</td>
                          <td className="px-4 py-2.5 text-right sm:text-sm text-xs text-slate-600">{(resource.unit_price || 0).toLocaleString('vi-VN')}</td>
                          <td className="px-4 py-2.5 text-right"></td>
                        </tr>
                      ))
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xóa bước quy trình?"
        message="Tài nguyên trong bước này cũng sẽ bị xóa."
        confirmText="Xóa"
        isDestructive
        isLoading={isDeletingStep}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function StepForm({ templateId, companyId, step, isSaving, onCancel, onSubmit }: {
  templateId: string;
  companyId: string;
  step: WorkProcessStep | null;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (payload: any) => Promise<void>;
}) {
  const [form, setForm] = useState({
    step_code: step?.step_code || '',
    step_name: step?.step_name || '',
    category_code: step?.category_code || 'construction',
    unit: step?.unit || 'cây',
    default_quantity: step?.default_quantity || 1,
    estimate_item_mode: step?.estimate_item_mode || 'child',
    sort_order: step?.sort_order || 1,
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await onSubmit({ ...form, company_id: companyId, template_id: templateId, default_quantity: Number(form.default_quantity || 0), sort_order: Number(form.sort_order || 0) });
  };

  return (
    <Card className="mb-4 border-emerald-200 bg-white p-4 shadow-sm">
      <form onSubmit={submit} className="grid gap-3 lg:grid-cols-6">
        <input value={form.step_code} onChange={(event) => setForm({ ...form, step_code: event.target.value })} placeholder="Mã bước" className="field" />
        <input required value={form.step_name} onChange={(event) => setForm({ ...form, step_name: event.target.value })} placeholder="Tên bước" className="field lg:col-span-2" />
        <input required value={form.unit} onChange={(event) => setForm({ ...form, unit: event.target.value })} placeholder="ĐVT" className="field" />
        <input type="number" min="0" step="0.0001" value={form.default_quantity} onChange={(event) => setForm({ ...form, default_quantity: Number(event.target.value) })} className="field" />
        <select value={form.estimate_item_mode} onChange={(event) => setForm({ ...form, estimate_item_mode: event.target.value as any })} className="field">
          <option value="child">Tạo hạng mục con</option>
          <option value="parent">Tạo hạng mục ngang cấp</option>
          <option value="resource_only">Chỉ thêm tài nguyên</option>
        </select>
        <div className="flex gap-2 lg:col-span-6">
          <Button type="submit" isLoading={isSaving}>{step ? 'Lưu bước' : 'Thêm bước'}</Button>
          <Button type="button" variant="secondary" onClick={onCancel}>Hủy</Button>
        </div>
      </form>
    </Card>
  );
}

function ResourceEditor({ step, isSaving, onSave, onCancel }: { step: WorkProcessStep; isSaving: boolean; onSave: (resources: ProcessStepResourceInput[]) => Promise<void>; onCancel?: () => void }) {
  const [resources, setResources] = useState<ProcessStepResourceInput[]>(
    (step.resources || []).map((resource) => ({
      company_id: step.company_id,
      template_id: step.template_id,
      step_id: step.id,
      resource_type: resource.resource_type,
      resource_id: resource.resource_id,
      resource_code: resource.resource_code,
      resource_name: resource.resource_name,
      unit: resource.unit,
      norm_quantity: resource.norm_quantity,
      waste_factor: resource.waste_factor,
      price_source: resource.price_source,
      unit_price: resource.unit_price,
      price_date: resource.price_date,
      sort_order: resource.sort_order,
    })),
  );

  const update = (index: number, patch: Partial<ProcessStepResourceInput>) => {
    setResources((current) => current.map((resource, idx) => (idx === index ? { ...resource, ...patch } : resource)));
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="sm:text-sm text-xs font-medium text-slate-800">Tài nguyên bước</h4>
        <Button size="sm" variant="secondary" onClick={() => setResources([...resources, { ...blankResource, company_id: step.company_id, template_id: step.template_id, step_id: step.id, sort_order: resources.length + 1 }])}>
          <i className="ri-add-line mr-2" />
          Thêm dòng
        </Button>
      </div>
      <div className="space-y-2">
        {resources.map((resource, index) => (
          <div key={index} className="grid gap-2 rounded-md border border-slate-100 bg-slate-50 p-2 lg:grid-cols-[140px_1fr_90px_110px_110px_120px_40px]">
            <select value={resource.resource_type} onChange={(event) => update(index, { resource_type: event.target.value })} className="field">
              <option value="material">Vật tư</option>
              <option value="labor">Nhân công</option>
              <option value="equipment">Thiết bị</option>
              <option value="agro">Phân thuốc</option>
              <option value="service">Dịch vụ</option>
              <option value="subcontract">Thầu phụ</option>
            </select>
            <input value={resource.resource_name} onChange={(event) => update(index, { resource_name: event.target.value })} placeholder="Tên tài nguyên" className="field" />
            <input value={resource.unit} onChange={(event) => update(index, { unit: event.target.value })} placeholder="ĐVT" className="field" />
            <input type="number" value={resource.norm_quantity} onChange={(event) => update(index, { norm_quantity: Number(event.target.value) })} className="field" />
            <input type="number" value={resource.waste_factor} onChange={(event) => update(index, { waste_factor: Number(event.target.value) })} className="field" />
            <input type="number" value={resource.unit_price} onChange={(event) => update(index, { unit_price: Number(event.target.value) })} className="field" />
            <Button size="icon" variant="ghost" onClick={() => setResources(resources.filter((_, idx) => idx !== index))}>
              <i className="ri-delete-bin-line text-rose-600" />
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        {onCancel && <Button variant="secondary" onClick={onCancel}>Hủy</Button>}
        <Button isLoading={isSaving} onClick={() => onSave(resources.filter((resource) => resource.resource_name.trim() && resource.unit.trim()))}>Lưu tài nguyên</Button>
      </div>
    </div>
  );
}
